import type { ImeiHubServiceId } from '../components/ImeiHubSignalIcon'
import { imeiAPI } from '../api/client'
import type { ImeiCheckPayload } from './imeiReportModel'
import { notifyImeiReportsUpdated } from './imeiReportsUnread'

const STORAGE_KEY = 'redevicer:pending-imei-report'
const MAX_AGE_MS = 2 * 60 * 60 * 1000

export type PendingImeiReport = {
  imei: string
  service: ImeiHubServiceId
  checkedAt: string
  result: ImeiCheckPayload
}

export function storePendingImeiReport(payload: PendingImeiReport): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
  } catch {
    /* ignore quota / private mode */
  }
}

export function readPendingImeiReport(): PendingImeiReport | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as PendingImeiReport
    if (!parsed?.imei || !parsed?.checkedAt || !parsed?.result) return null
    const age = Date.now() - Date.parse(parsed.checkedAt)
    if (!Number.isFinite(age) || age < 0 || age > MAX_AGE_MS) {
      clearPendingImeiReport()
      return null
    }
    return parsed
  } catch {
    return null
  }
}

export function clearPendingImeiReport(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    /* ignore */
  }
}

/** After login — save the latest guest check to the user profile (no second IMEI.EU charge). */
export async function claimPendingImeiReport(token: string): Promise<boolean> {
  const pending = readPendingImeiReport()
  if (!pending) return false

  try {
    await imeiAPI.claimReport(token, {
      imei: pending.imei,
      service: pending.service,
      is_valid: Boolean(pending.result.is_valid),
      is_stolen:
        typeof pending.result.is_stolen === 'boolean' ? pending.result.is_stolen : null,
      brand: pending.result.brand ?? null,
      model: pending.result.model ?? null,
      checked_at: pending.checkedAt,
    })
    clearPendingImeiReport()
    window.dispatchEvent(new CustomEvent('redevicer:imei-report-claimed'))
    notifyImeiReportsUpdated()
    return true
  } catch {
    return false
  }
}
